import { useContext, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { SearchContext } from "../../../Context/Search";
import {
  searchAction,
  clearSearchAction,
} from "../../../Context/Search/actions";
import { ReactComponent as CloseIcon } from "../../../Assets/Icons/close.svg";
import routes from "../../../utils/routes";
import "./searchOverlay.css";

const RESULT_TYPE_CONFIG = {
  blogs: {
    label: "Blog Posts",
    getPath: (item) => `${routes.singleBlog}/${item.id}`,
  },
  legal_documents: {
    label: "Legal Documents",
    getPath: () => routes.legalFramework,
  },
  infographics: {
    label: "Infographics",
    getPath: () => routes.infographics,
  },
  policy_briefs: {
    label: "Policy Briefs",
    getPath: () => routes.policybrief,
  },
  videos: {
    label: "Videos",
    getPath: () => routes.videos,
  },
  data_categories: {
    label: "Data",
    getPath: (item) => `${routes.innerData}/${item.id}`,
  },
};

const SearchOverlay = ({ isOpen, onClose }) => {
  const { searchState, dispatch } = useContext(SearchContext);
  const { searching, searchResults, query } = searchState;
  const inputRef = useRef(null);

  const debouncedSearch = useRef(
    debounce((value) => {
      if (value.trim().length >= 2) {
        dispatch(searchAction({ q: value.trim() }));
      }
    }, 400)
  );

  const handleInputChange = (e) => {
    debouncedSearch.current(e.target.value);
  };

  const handleClose = () => {
    dispatch(clearSearchAction());
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(clearSearchAction());
    }
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const hasResults =
    searchResults &&
    Object.values(searchResults).some((arr) => arr.length > 0);

  return (
    <div
      className="search-overlay fixed inset-0 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white w-full max-w-2xl mx-auto mt-20 rounded-lg search-overlay__card overflow-hidden mx-4 md:mx-auto">
        <div className="flex items-center px-6 py-4 border-b border-gray-200">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search blogs, documents, videos, data..."
            className="flex-1 font-sans text-sm text-chaos outline-none"
            onChange={handleInputChange}
          />
          <button
            onClick={handleClose}
            className="ml-4 cursor-pointer outline-none"
            aria-label="Close search"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto px-6 py-4">
          {searching && (
            <p className="text-gray-500 text-sm font-sans">Searching...</p>
          )}
          {!searching && query && !hasResults && (
            <p className="text-gray-500 text-sm font-sans">
              No results found for "{query}"
            </p>
          )}
          {!searching &&
            hasResults &&
            Object.entries(searchResults).map(([type, items]) => {
              if (!items.length) return null;
              const config = RESULT_TYPE_CONFIG[type];
              if (!config) return null;
              return (
                <div key={type} className="mb-4">
                  <h4 className="text-fig font-sans text-xs uppercase tracking-wide mb-2 font-semibold">
                    {config.label}
                  </h4>
                  <ul>
                    {items.map((item) => (
                      <li key={item.id} className="mb-2">
                        <Link
                          to={config.getPath(item)}
                          onClick={handleClose}
                          className="text-chaos hover:text-fig text-sm font-sans block py-1"
                        >
                          {item.title || item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
